import React, { type RefObject, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { CircularProgress, Grid } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { type Field, type Nil, Text } from '@pbt/pbt-ui-components'
import { Warning as WarningIcon } from '@pbt/pbt-ui-components/src/icons'

import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import type { PrescriptionWorkflowType } from '~/constants/PrescriptionWorkflow'
import { getCurrentBusinessIsOmniChannel } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import type { Prescription, SignatureData } from '~/types'
import { getWorkflowType } from '~/utils/prescription'
import useDialog from '~/utils/useDialog'

import type { PrescriptionActionContextType } from '../PrescriptionContext'
import type { AdministrationSectionSimpleHandle } from './AdministrationSectionSimple'
import { PrescriptionChewyActiveRxBody } from './body/PrescriptionChewyActiveRxBody'
import { PrescriptionOtherWorkflowsBody } from './body/PrescriptionOtherWorkflowsBody'
import SwapProductReactiveForm, {
  type SwapProductReactiveFormHandle,
} from './body/SwapProductReactiveForm'
import type { SwapProductRefillHandle } from './body/SwapProductRefill'
import type { ChargeSectionHandle } from './ChargeSection'
import type { ControlledSubstanceSectionHandle } from './ControlledSubstanceSection'
import type { DiscussedWithClientFieldHandle } from './DiscussedWithClientField'
import type { DispensedFromSectionHandle } from './DispensedFromSection'
import type { DrugCompoundInfoHandle } from './DrugCompoundInfo'
import type { PrescriptionDetailsSectionHandle } from './PrescriptionDetailsSection'
import type { QuantitySectionHandle } from './QuantitySection'
import type { RefillableSectionDataHandle } from './RefillableSection'
import type { TaskSectionHandle } from './TaskSection'
import type { VariationsTableProps } from './VariationsTable'
import WorkflowSection from './workflow-section/WorkflowSection'

const useStyles = makeStyles(
  (theme) => ({
    loader: {
      height: `calc(80vh - ${theme.spacing(17)})`, // approximate dialog height - bottom and header height
    },
    mismatchWarning: {
      color: 'white',
      backgroundColor: theme.colors.alertLabelError,
      display: 'flex',
      alignItems: 'center',
    },
    warningIcon: {
      marginRight: theme.spacing(2),
      width: '2rem',
      height: '2rem',
    },
    expandCollapse: {
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
    },
    teamMemberSelect: {
      backgroundColor: 'white',
    },
  }),
  { name: 'PrescriptionBody' },
)

export interface PrescriptionBodyProps {
  activeWorkflow?: PrescriptionWorkflowType
  areChargesPostedAndEditable?: boolean
  automaticallyCreateTaskLabel?: string
  clientId: string
  currentDoctorHasDvmLicense: boolean | null // null - no doctor selected
  currentDoctorHasMatchingStateLicense: boolean | null // null - no doctor selected, loading, or feature disabled
  currentDoctorId: string | Nil
  disableWorkflowSelector?: boolean
  fields: {
    automaticallyCreateTask: Field
    autoshipFrequency: Field
    autoshipUnitId: Field
    chewyItem: Field
    compoundingReason: Field
    compoundingReasonId: Field
    doctorId: Field
    enableAutoship: Field
    saveAsDefault: Field
    teamMemberId: Field
  }
  handleSave: PrescriptionActionContextType['onSave']
  handleSetActiveWorkflow: (
    workflow: PrescriptionWorkflowType | undefined,
  ) => void
  handleVariationChange: VariationsTableProps['onVariationChange']
  isChewyCatalogItemNotLinked: boolean
  isCompoundingReasonRequired?: boolean
  isCustomCompound?: boolean
  isDvmLicenseRequired: boolean
  isEdit: boolean
  isLengthOfCourseRequired: boolean
  isRxPrescription: boolean
  isTemplatesLoaded: boolean
  isUserSignatureAreaEnabled?: boolean
  isVetDiet: boolean
  onRefChange?: () => void
  /**
   * Callback fired when signature is updated via signature dialog.
   * Used in swap flow where skipOrderFetching prevents normal save flow.
   * @param signerId - ID of the doctor who signed
   * @param signatureUrl - Base64-encoded PNG signature data
   */
  onSignatureUpdate?: (signerId: string, signatureUrl: string) => void
  outsideSoap: boolean
  patientId: string
  prescription: Prescription
  refs: {
    administrationSectionRef: RefObject<
      | AdministrationSectionSimpleHandle
      | PrescriptionDetailsSectionHandle
      | SwapProductReactiveFormHandle
    >
    chargeSectionRef: RefObject<ChargeSectionHandle>
    controlledSubstanceSectionRef: RefObject<ControlledSubstanceSectionHandle>
    discussedWithClientRef: RefObject<DiscussedWithClientFieldHandle>
    dispensedFromSectionRef: RefObject<DispensedFromSectionHandle>
    drugCompoundInfoRef: RefObject<DrugCompoundInfoHandle>
    quantitySectionRef: RefObject<QuantitySectionHandle>
    refillableSectionRef: RefObject<
      RefillableSectionDataHandle | SwapProductRefillHandle
    >
    taskSectionRef: RefObject<TaskSectionHandle>
  }
  showAutomaticallyCreateTaskOption?: boolean
  showDiscussedWithClient?: boolean
  showDoctorSelect: boolean
  showOnlyDrugVariation?: boolean
  signatureData: SignatureData
  simpleAdministrationInstructions?: boolean
  validate: () => boolean
}

const PrescriptionBody = ({
  areChargesPostedAndEditable,
  activeWorkflow,
  automaticallyCreateTaskLabel,
  clientId,
  currentDoctorHasDvmLicense,
  currentDoctorHasMatchingStateLicense,
  currentDoctorId,
  fields: {
    automaticallyCreateTask,
    chewyItem,
    compoundingReason,
    compoundingReasonId,
    doctorId,
    saveAsDefault,
    teamMemberId,
    autoshipFrequency,
    autoshipUnitId,
    enableAutoship,
  },
  handleSave,
  handleVariationChange,
  disableWorkflowSelector,
  isChewyCatalogItemNotLinked,
  isCompoundingReasonRequired,
  isCustomCompound,
  isDvmLicenseRequired,
  isEdit,
  isLengthOfCourseRequired,
  isRxPrescription,
  isTemplatesLoaded,
  isUserSignatureAreaEnabled,
  isVetDiet,
  onRefChange,
  onSignatureUpdate,
  outsideSoap,
  patientId,
  prescription,
  refs: {
    administrationSectionRef,
    chargeSectionRef,
    controlledSubstanceSectionRef,
    discussedWithClientRef,
    dispensedFromSectionRef,
    drugCompoundInfoRef,
    quantitySectionRef,
    refillableSectionRef,
    taskSectionRef,
  },
  handleSetActiveWorkflow,
  showAutomaticallyCreateTaskOption,
  showDiscussedWithClient,
  showDoctorSelect,
  showOnlyDrugVariation,
  signatureData,
  simpleAdministrationInstructions,
  validate,
}: PrescriptionBodyProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'Dialogs', 'Tasks'])

  const [activeAdminFrequencyId, setActiveAdminFrequencyId] = useState<
    string | undefined
  >(prescription.administrationFrequencyId)

  const [openSignatureDialog] = useDialog(DialogNames.DOCTOR_SIGNATURE_DIALOG)

  // Feature toggle, CVC clinic detection, and task type check for swap buttons
  const isSwapReactiveRxEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SWAP_REACTIVE_RX),
  )
  const isOmniChannel = useSelector(getCurrentBusinessIsOmniChannel)

  const { isChewyReactiveRx, isChewyActiveRx } = getWorkflowType(activeWorkflow)

  const isControlledSubstance = Boolean(prescription.inventory?.controlled)

  const currentFlowIsSwapProduct =
    isSwapReactiveRxEnabled && isOmniChannel && isChewyReactiveRx

  const acceptPrescriptionSignature = (
    signerId: string,
    signaturePngString: string,
  ) => {
    handleSave(
      {
        isActiveRxSaveDraft: isChewyActiveRx,
        signatureInfo: {
          signature: signaturePngString,
          signerId,
        },
      },
      false,
    )

    if (onSignatureUpdate) {
      onSignatureUpdate(signerId, signaturePngString)
    }
  }

  const handleSignatureEdit = () => {
    if (isChewyActiveRx || validate()) {
      openSignatureDialog({
        initialSignatureUrl: signatureData.signatureUrl,
        initialSignerValue: signatureData.signerId || currentDoctorId,
        disabled: isChewyActiveRx,
        enableSignerSignature: isUserSignatureAreaEnabled,
        outputFormat: 'png',
        onSign: acceptPrescriptionSignature,
      })
    }
  }

  const handleSetActiveAdminFrequencyId = (id: string) => {
    setActiveAdminFrequencyId(id)
  }

  if (!isTemplatesLoaded) {
    return (
      <Grid
        container
        alignItems="center"
        className={classes.loader}
        direction="column"
        justifyContent="center"
      >
        <CircularProgress size={32} />
      </Grid>
    )
  }

  return (
    <Grid container direction="column" p={2}>
      {prescription.mismatchWarning && (
        <Text className={classes.mismatchWarning} mb={2} px={1} variant="h1">
          <WarningIcon className={classes.warningIcon} />
          {prescription.mismatchWarning}
        </Text>
      )}
      {!currentFlowIsSwapProduct && (
        <>
          <Text strong mb={1} variant="subheading3">
            {t('Common:WORKFLOW_ONE')}
          </Text>
          <Grid item mb={2}>
            <WorkflowSection
              activeWorkflow={activeWorkflow}
              areChargesPostedAndEditable={areChargesPostedAndEditable}
              clientId={clientId}
              disableWorkflowSelector={disableWorkflowSelector}
              handleSetActiveWorkflow={handleSetActiveWorkflow}
              isChewyReactiveRx={isChewyReactiveRx}
              isCustomCompound={isCustomCompound}
              prescription={prescription}
              onChange={handleSetActiveWorkflow}
            />
          </Grid>
        </>
      )}

      {/* Render different body components based on workflow type */}
      {currentFlowIsSwapProduct && (
        /* Simplified editor for Chewy Reactive Rx (Approve/Decline/Save Draft buttons) */
        <SwapProductReactiveForm
          currentDoctorHasDvmLicense={currentDoctorHasDvmLicense}
          currentDoctorHasMatchingStateLicense={
            currentDoctorHasMatchingStateLicense
          }
          fields={{
            automaticallyCreateTask,
            compoundingReason,
            compoundingReasonId,
            doctorId,
            teamMemberId,
          }}
          isCompoundingReasonRequired={isCompoundingReasonRequired}
          isRxPrescription={isRxPrescription}
          isVetDiet={isVetDiet}
          prescription={prescription}
          ref={
            administrationSectionRef as RefObject<SwapProductReactiveFormHandle>
          }
          refillableSectionRef={refillableSectionRef}
          showAutomaticallyCreateTaskOption={showAutomaticallyCreateTaskOption}
          taskSectionRef={taskSectionRef}
          onRefChange={onRefChange}
        />
      )}
      {!currentFlowIsSwapProduct && isChewyActiveRx && (
        // Full editor for Chewy Active Rx
        <PrescriptionChewyActiveRxBody
          activeAdminFrequencyId={activeAdminFrequencyId}
          activeWorkflow={activeWorkflow}
          automaticallyCreateTaskLabel={automaticallyCreateTaskLabel}
          clientId={clientId}
          currentDoctorHasDvmLicense={currentDoctorHasDvmLicense}
          currentDoctorHasMatchingStateLicense={
            currentDoctorHasMatchingStateLicense
          }
          fields={{
            automaticallyCreateTask,
            chewyItem,
            compoundingReason,
            compoundingReasonId,
            doctorId,
            autoshipFrequency,
            autoshipUnitId,
            enableAutoship,
          }}
          isCompoundingReasonRequired={isCompoundingReasonRequired}
          isControlledSubstance={isControlledSubstance}
          isCustomCompound={isCustomCompound}
          isEdit={isEdit}
          isRxPrescription={isRxPrescription}
          isVetDiet={isVetDiet}
          outsideSoap={outsideSoap}
          patientId={patientId}
          prescription={prescription}
          refs={{
            administrationSectionRef,
            controlledSubstanceSectionRef,
            discussedWithClientRef,
            dispensedFromSectionRef,
            drugCompoundInfoRef,
            quantitySectionRef,
            refillableSectionRef,
            taskSectionRef,
          }}
          showAutomaticallyCreateTaskOption={showAutomaticallyCreateTaskOption}
          showDiscussedWithClient={showDiscussedWithClient}
          signatureData={signatureData}
          simpleAdministrationInstructions={simpleAdministrationInstructions}
          onActiveAdminFrequencyChange={setActiveAdminFrequencyId}
          onRefChange={onRefChange}
          onSignatureEdit={handleSignatureEdit}
          onVariationChange={handleVariationChange}
        />
      )}
      {!currentFlowIsSwapProduct && !isChewyActiveRx && (
        // Full editor for other workflows (Order, In-house Rx, etc.)
        <PrescriptionOtherWorkflowsBody
          activeAdminFrequencyId={activeAdminFrequencyId}
          activeWorkflow={activeWorkflow}
          areChargesPostedAndEditable={areChargesPostedAndEditable}
          automaticallyCreateTaskLabel={automaticallyCreateTaskLabel}
          clientId={clientId}
          currentDoctorHasDvmLicense={currentDoctorHasDvmLicense}
          currentDoctorHasMatchingStateLicense={
            currentDoctorHasMatchingStateLicense
          }
          fields={{
            automaticallyCreateTask,
            compoundingReason,
            compoundingReasonId,
            doctorId,
            saveAsDefault,
            teamMemberId,
          }}
          isChewyCatalogItemNotLinked={isChewyCatalogItemNotLinked}
          isCompoundingReasonRequired={isCompoundingReasonRequired}
          isControlledSubstance={isControlledSubstance}
          isCustomCompound={isCustomCompound}
          isDvmLicenseRequired={isDvmLicenseRequired}
          isEdit={isEdit}
          isLengthOfCourseRequired={isLengthOfCourseRequired}
          isRxPrescription={isRxPrescription}
          isVetDiet={isVetDiet}
          outsideSoap={outsideSoap}
          patientId={patientId}
          prescription={prescription}
          refs={{
            administrationSectionRef,
            chargeSectionRef,
            controlledSubstanceSectionRef,
            discussedWithClientRef,
            dispensedFromSectionRef,
            drugCompoundInfoRef,
            quantitySectionRef,
            refillableSectionRef,
            taskSectionRef,
          }}
          showAutomaticallyCreateTaskOption={showAutomaticallyCreateTaskOption}
          showDiscussedWithClient={showDiscussedWithClient}
          showDoctorSelect={showDoctorSelect}
          showOnlyDrugVariation={showOnlyDrugVariation}
          signatureData={signatureData}
          simpleAdministrationInstructions={simpleAdministrationInstructions}
          onRefChange={onRefChange}
          onSetActiveAdminFrequencyId={handleSetActiveAdminFrequencyId}
          onSignatureEdit={handleSignatureEdit}
          onVariationChange={handleVariationChange}
        />
      )}
    </Grid>
  )
}

export default PrescriptionBody
